import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAppCopy } from '@/i18n/context';

/**
 * Asks before an action the person started and cannot take back (DESIGN-SPEC
 * §6.10, §7.1): archive an agent, revoke an invitation, delete a memory, remove
 * a person. Cancel is the safe outlined choice; the confirm names the act
 * ("Archive Weekly price gap review", "Revoke invitation"), never a bare
 * "Confirm". While the action runs, nothing closes the dialog.
 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  tone = 'danger',
  busy = false,
  error,
  className,
  onCloseAutoFocus,
  onCancel,
  onConfirm,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  /** `danger` for destructive acts; `default` when the act is the dialog's one primary. */
  tone?: 'danger' | 'default';
  busy?: boolean;
  error?: string;
  className?: string;
  /** Menu-launched dialogs return focus to their original action button. */
  onCloseAutoFocus?: (event: Event) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { cancel } = useAppCopy();
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onCancel();
      }}
    >
      <DialogContent
        className={`pc-portal ${className ?? ''}`}
        showCloseButton={!busy}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogTitle className="pr-4 leading-snug [overflow-wrap:anywhere]">{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
        {error && (
          <p className="ws-error" role="alert">
            {error}
          </p>
        )}
        <DialogFooter className="grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:justify-end">
          <Button
            type="button"
            className="w-full sm:w-auto"
            variant="outline"
            disabled={busy}
            onClick={onCancel}
          >
            {cancel}
          </Button>
          <Button
            type="button"
            className={
              tone === 'danger'
                ? 'pc-confirm-danger h-auto min-h-9 w-full whitespace-normal text-center sm:w-auto'
                : 'h-auto min-h-9 w-full whitespace-normal text-center sm:w-auto'
            }
            variant={tone === 'danger' ? 'destructive' : 'default'}
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
